<template>
  <div
    v-on-click-outside="closeMenu"
    data-cy="notes-dropdown"
    class="relative flex flex-1 flex-col"
  >
    <button
      type="button"
      aria-haspopup="listbox"
      aria-expanded="true"
      aria-labelledby="listbox-label"
      class="relative flex w-full items-center justify-between rounded-sm border bg-white py-1 pr-2 pl-2 text-left text-sm text-headline-new capitalize"
      :class="[
        showMenu
          ? 'border-blue-500 hover:border-blue-500'
          : 'hover:border-gray-300',
        style.buttonHeight,
      ]"
      @click.stop="toggleMenu"
    >
      <div class="flex items-center space-x-2">
        <div>
          <div
            class="h-1-5 w-1-5 rounded-full"
            :style="`background-color:rgb(${getBackgroundColor(selectedVal)})`"
          />
        </div>
        <div
          v-tooltip="{
            content: selectedOption,
            placement: 'left',
            onShow: () => selectedOption || selectedOption.length >= 25,
          }"
          class="ellipsis ml-1"
        >
          {{ selectedOption || placeholder }}
        </div>
      </div>
      <icon-base
        height="6"
        width="10"
        :icon="IconDropdownArrow"
        class="transform text-headline-new"
        :class="{ 'rotate-180': showMenu }"
      />
    </button>
    <ul
      v-show="showMenu"
      tabindex="-1"
      role="listbox"
      aria-labelledby="listbox-label"
      aria-activedescendant="listbox-item-3"
      class="absolute top-13 left-0 z-50 max-h-68 w-full overflow-y-auto rounded-md bg-white p-2 pt-2 text-sm shadow-lg ring-1 ring-black/5 focus:outline-hidden sm:text-sm"
    >
      <li
        v-for="(option, index) in options"
        :key="option.id"
        v-tooltip="{
          content: option.name,
          placement: 'left',
          onShow: () => option.name.length > 30,
        }"
        class="flex items-center justify-start overflow-auto py-2 pl-3 font-normal capitalize"
        :aria-selected="isSelected(option.id)"
        :class="[
          { 'bg-gray-100': isSelected(option.id) },
          isPhaseActive(index)
            ? `cursor-pointer text-gray-600 ${style.optionHoverBgColor}`
            : 'cursor-not-allowed text-gray-400',
        ]"
        :data-cy="`notes-dropdown-option-${index}`"
        @click="isPhaseActive(index) && selectOption(option.id)"
      >
        <div
          class="h-1-5 w-1-5 rounded-full"
          :style="
            isPhaseActive(index)
              ? `background-color:rgb(${getBackgroundColor(option.id)})`
              : `background-color:rgb(${COLORS[15]})`
          "
        />
        <span class="ellipsis ml-1 whitespace-nowrap">
          {{ option.name }}
        </span>
      </li>
    </ul>
  </div>
</template>

<script lang="ts">
import { computed, onMounted, ref, watch } from "vue";

import sample from "lodash/sample";

import { COLORS } from "@/helpers/constants";

export interface Option {
  id: string;
  name: string;
}

interface Props {
  modelValue?: string | null;
  options: Option[];
  placeholder?: string;
  colorsForOptions?: Record<string, number>;
  disabled?: boolean;
  activeFlowIndex?: number;
  style?: {
    buttonHeight?: string;
    optionHoverBgColor?: string;
  };
}
</script>

<script setup lang="ts">
import IconDropdownArrow from "@/components/icons/IconDropdownArrow.vue";

const {
  modelValue,
  options,
  colorsForOptions,
  disabled,
  activeFlowIndex,
  style = { buttonHeight: "h-9", optionHoverBgColor: "hover:bg-gray-100" },
} = defineProps<Props>();
const emit = defineEmits<{
  "update:modelValue": [value: string];
}>();

const showMenu = ref(false);
const selectedVal = ref(modelValue || "");

const selectedOption = computed(() => {
  const option = options?.find((option) => option.id === selectedVal.value);
  return option?.name ?? "";
});

const isSelected = (id: string) => selectedVal.value === id;

const isPhaseActive = (phaseIndex: number) => {
  const isForDealNotes = activeFlowIndex !== undefined;
  return (
    phaseIndex === 0 ||
    !isForDealNotes ||
    (isForDealNotes && phaseIndex <= activeFlowIndex)
  );
};

const selectOption = (id: string) => {
  if (disabled) {
    return;
  }
  selectedVal.value = id;
  toggleMenu();
  emit("update:modelValue", id);
};

const toggleMenu = () => {
  showMenu.value = !showMenu.value;
};

const closeMenu = () => {
  showMenu.value = false;
};

const getBackgroundColor = (id: string) => {
  if (colorsForOptions) {
    const option = colorsForOptions[id];
    return COLORS[option];
  }
  return sample(COLORS);
};

onMounted(() => {
  if (
    !selectedVal.value &&
    activeFlowIndex !== undefined &&
    activeFlowIndex >= 0 &&
    options.length
  ) {
    selectedVal.value = options[activeFlowIndex].id;
    emit("update:modelValue", selectedVal.value);
  }
});

watch(
  () => modelValue,
  (newValue) => {
    if (newValue) {
      selectedVal.value = newValue;
    }
  }
);
</script>

<style scoped>
@reference "@/assets/tailwind.css";

.ellipsis {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
